/**
 * Staging proposals store — KAIROS queue.
 *
 * Nothing in this table is auto-applied. KAIROS writes proposals,
 * analyst resolves them via the digest panel.
 */

import { getDatabase } from '@/storage/database';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProposalType = 'cross_link' | 'contradiction' | 'dead_thread' | 'pattern_elevation';
export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

export interface StagingProposal {
  id: string;
  proposalType: ProposalType;
  sourceInvestigationId: string | null;
  targetInvestigationId: string | null;
  entityIds: string[];
  summary: string;
  detail: Record<string, unknown>;
  confidence: number;
  tier: number;
  status: ProposalStatus;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function db() {
  const conn = getDatabase();
  if (!conn) throw new Error('Database not initialized');
  return conn;
}

interface ProposalRow {
  id: string;
  proposal_type: string;
  source_investigation_id: string | null;
  target_investigation_id: string | null;
  entity_ids: string | null;
  summary: string;
  detail: string;
  confidence: number;
  tier: number;
  status: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

function rowToProposal(row: ProposalRow): StagingProposal {
  return {
    id: row.id,
    proposalType: row.proposal_type as ProposalType,
    sourceInvestigationId: row.source_investigation_id,
    targetInvestigationId: row.target_investigation_id,
    entityIds: JSON.parse(row.entity_ids || '[]'),
    summary: row.summary,
    detail: JSON.parse(row.detail || '{}'),
    confidence: row.confidence,
    tier: row.tier,
    status: row.status as ProposalStatus,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at,
    resolvedBy: row.resolved_by,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new staging proposal (status defaults to 'pending'). */
export async function createProposal(
  proposal: Omit<StagingProposal, 'status' | 'resolvedAt' | 'resolvedBy'>,
): Promise<void> {
  await db().execute(
    `INSERT INTO staging_proposals (id, proposal_type, source_investigation_id, target_investigation_id,
     entity_ids, summary, detail, confidence, tier, status, created_at)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, 'pending', $10)`,
    [
      proposal.id,
      proposal.proposalType,
      proposal.sourceInvestigationId,
      proposal.targetInvestigationId,
      JSON.stringify(proposal.entityIds),
      proposal.summary,
      JSON.stringify(proposal.detail),
      proposal.confidence,
      proposal.tier,
      proposal.createdAt,
    ],
  );
}

/** Resolve a proposal (accept or reject). */
export async function resolveProposal(
  id: string,
  status: 'accepted' | 'rejected',
  resolvedBy: string,
): Promise<void> {
  await db().execute(
    `UPDATE staging_proposals SET status = $1, resolved_at = $2, resolved_by = $3 WHERE id = $4`,
    [status, new Date().toISOString(), resolvedBy, id],
  );
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Get all pending proposals, ordered by confidence descending. */
export async function getPendingProposals(): Promise<StagingProposal[]> {
  const rows = await db().query<ProposalRow>(
    `SELECT * FROM staging_proposals WHERE status = 'pending' ORDER BY confidence DESC`,
  );
  return rows.map(rowToProposal);
}

/** Get count of pending proposals (for badge display). */
export async function getPendingCount(): Promise<number> {
  const rows = await db().query<{ count: number }>(
    `SELECT COUNT(*) as count FROM staging_proposals WHERE status = 'pending'`,
  );
  return rows[0]?.count ?? 0;
}

/** Get proposals by type (for grouped display in digest panel). */
export async function getProposalsByType(proposalType: ProposalType): Promise<StagingProposal[]> {
  const rows = await db().query<ProposalRow>(
    `SELECT * FROM staging_proposals WHERE proposal_type = $1 AND status = 'pending' ORDER BY confidence DESC`,
    [proposalType],
  );
  return rows.map(rowToProposal);
}
